import { Box, Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { addFnolRisk, setErsClaim, setGlassClaim } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { getClaimRisks, getPartnerId, getServicingOpCo, userIsAdjuster } from '../../../app/store/combinedSelectors';
import {
  cancelBtn,
  continueBtn,
  FNOLCancelConfirmationConstants,
  GenericModalTopMargin,
} from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import getCopyById from '../../../copy/copyMapper';
import { GenericProductType } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import GenericModal from '../../shared/GenericModal/GenericModal';
import RiskOptionList from './RiskOptionList';

export default function RiskSelection() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);
  const isAdjuster = useSelector(userIsAdjuster);
  const riskOptions = useSelector(getClaimRisks);
  const copy = getCopyById(opco, partnerId);

  const claimsData = useAppSelector((state) => state.claims);

  const [riskSelected, setRiskSelected] = React.useState(riskOptions.length > 0);
  const [risk, setRisk] = React.useState<any>(riskOptions.length > 0 ? riskOptions[0] : null);
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * This check identifies if there are no claims available in the Redux which represents we need to refresh the claims data from the CC
   */
  const isClaimRefreshNeeded =
    claimsData.draftClaims?.length === 0 &&
    claimsData.openClaims?.length === 0 &&
    claimsData.closedClaims?.length === 0;

  useEffect(() => {
    /**
     * Reset glass and ERS claim flags when entering risk selection
     * This ensures flags don't persist when starting a new claim
     */
    dispatch(setGlassClaim({ isGlassClaim: false }));
    dispatch(setErsClaim({ isErsClaim: false }));

    if (riskOptions.length === 0) {
      setShowErrorBanner(true);
    } else {
      setShowErrorBanner(false);
    }
  }, [riskOptions, dispatch]);

  function launchPrivacyPolicy(event: any) {
    event.preventDefault();
    window.open(copy.privacyAndSecurityLink, '_blank');
  }

  function handleNext() {
    dispatch(
      addFnolRisk({
        riskInfo: risk,
      })
    );
    if (isAdjuster) {
      navigate('/confirm-insureds');
    } else if (risk.generalizedProductType === GenericProductType.Auto) {
      navigate('/driver-selection');
    } else {
      navigate('/time-location-selection');
    }
  }

  function handleRiskSelection(risk: any) {
    setRisk(risk);
    setRiskSelected(true);
  }

  function handleCancel() {
    if (isMobile && theme.custom.styles.showModalPopUpOnFNOLCancel) {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
      setCancelConfirmationModalOpen(true);
    } else {
      handleConfirmedCancel();
    }
  }

  function handleCloseButtonClick() {
    setCancelConfirmationModalOpen(false);
  }

  function handleConfirmedCancel() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: isClaimRefreshNeeded,
      },
    });
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!!cancelConfirmationModalOpen && (
        <GenericModal
          title={FNOLCancelConfirmationConstants.TITLE}
          description={FNOLCancelConfirmationConstants.DESCRIPTION}
          primaryButtonText={FNOLCancelConfirmationConstants.PRIMARY_BUTTON_TEXT}
          secondaryButtonText={FNOLCancelConfirmationConstants.SECONDARY_BUTTON_TEXT}
          verticalPositionForMobile={GenericModalTopMargin}
          alertBoxStyle={theme.custom.styles.genericModalCancelAlertBoxStyle}
          theme={theme}
          closeButtonActionHandler={handleCloseButtonClick}
          primaryButtonActionHandler={handleConfirmedCancel}
          secondaryButtonActionHandler={handleCloseButtonClick}
        ></GenericModal>
      )}

      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        {showErrorBanner && <ErrorBanner></ErrorBanner>}
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.titleType3,
            }}
          >
            Let's get started.
          </Typography>

          <Typography sx={{ marginTop: '1em' }}>
            We'll ask a few questions to gather some basics about what happened. Depending on the complexity of the
            incident, this should only take a few minutes, although complex incidents could take a bit longer.
          </Typography>
        </Grid>

        <Grid sx={{ marginTop: '1em' }} item xs={12}>
          <RiskOptionList
            setRisk={(risk: any) => handleRiskSelection(risk)}
            handleRiskNotOnPolicy={() => setRiskSelected(false)}
            risks={riskOptions}
          ></RiskOptionList>
        </Grid>
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonDisabled={!riskSelected}
            nextButtonContent={continueBtn}
            handleNext={handleNext}
            backButton={false}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={cancelBtn}
            handleCancel={handleCancel}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
